import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Checkbox,
  FormGroup,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  EventNote as EventNoteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification, createSuccessNotification } from './common/NotificationSystem';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionButton } from './common/PermissionButton';
import { PermissionWrapper } from './common/PermissionWrapper';
import { Subscription } from './Subscription';
import { ApiTokens } from './ApiTokens';

interface User {
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'Administrator' | 'PowerUser' | 'ReadOnly';
  status: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  tenantId?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const Settings: React.FC = () => {


  // Helper function to check if current user is editing their own administrator account
  const isEditingOwnAdminAccount = (user: User): boolean => {
    return user.role === 'Administrator' && currentUser?.username === user.userName;
  };

  const { showNotification } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { isReadOnly } = usePermissions();
  const theme = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  
  // Define tab names
  const TAB_NAMES = {
    USER_MANAGEMENT: 'user-management',
    SYSTEM_SETTINGS: 'system-settings', 
    ACCOUNT_SETTINGS: 'account-settings',
    SUBSCRIPTION: 'subscription',
    API_TOKENS: 'api-tokens'
  } as const;
  
  // Always derive current tab from URL - this is the source of truth
  const currentTabFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('tab') || TAB_NAMES.USER_MANAGEMENT;
  }, [location.search]);
  
  // Use state for tab value, but always sync from URL
  const [tabValue, setTabValue] = useState(currentTabFromUrl);
  
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'PowerUser' as 'Administrator' | 'PowerUser' | 'ReadOnly',
  });

  // Enhanced User Management State
  const [userFilter, setUserFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'ENABLED' | 'DISABLED' | 'FORCE_CHANGE_PASSWORD'>('all');
  const [userRoleFilter, setUserRoleFilter] = useState<'all' | 'Administrator' | 'PowerUser' | 'ReadOnly'>('all');
  const [userSortBy, setUserSortBy] = useState<'firstName' | 'email' | 'role' | 'status' | 'createdAt'>('firstName');
  const [userSortOrder, setUserSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showMassEditDialog, setShowMassEditDialog] = useState(false);
  const [massEditAction, setMassEditAction] = useState<'role' | 'status'>('role');
  const [massEditValue, setMassEditValue] = useState<string>('');

  // Scan Settings State
  const [scanSettings, setScanSettings] = useState({
    status: 'DISABLED',
    cron: '15 3 * * ? *',
    utc_cron: '15 3 * * ? *',
    timezone: 'UTC',
    offset: 0
  });
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [editingScanSettings, setEditingScanSettings] = useState(false);
  const [loadingScanSettings, setLoadingScanSettings] = useState(false);
  const [savingScanSettings, setSavingScanSettings] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');

  interface ScanLimitEventRow {
    accountId: string | null;
    resource: string | null;
    observed_count?: string | number;
  }
  const [scanLimitEvents, setScanLimitEvents] = useState<ScanLimitEventRow[]>([]);
  const [loadingScanEvents, setLoadingScanEvents] = useState(false);
  const [acknowledgingScanLimitKey, setAcknowledgingScanLimitKey] = useState<string | null>(null);
  const scanEventsLoadInFlight = useRef(false);
  
  // Validation state for time fields
  const [timeValidation, setTimeValidation] = useState({
    hour: { isValid: true, error: '' },
    minute: { isValid: true, error: '' }
  });

  // Account Settings - Allowed Services state
  interface AccountItem { accountId: string; account_name: string; regions?: string; allowed_regions?: string; allowerd_regions?: string }
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [supportedServices, setSupportedServices] = useState<Record<string, string[]>>({});
  const [loadingSupportedServices, setLoadingSupportedServices] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [accountServiceSettings, setAccountServiceSettings] = useState<{ allowed_resources: string[]; denied_resources: string[] }>({ allowed_resources: [], denied_resources: [] });
  const [loadingAccountServiceSettings, setLoadingAccountServiceSettings] = useState(false);
  const [editingAllowedServices, setEditingAllowedServices] = useState(false);
  const [savingAllowedServices, setSavingAllowedServices] = useState(false);
  const [collapsedServices, setCollapsedServices] = useState<Set<string>>(new Set());
  const [collapseInitialized, setCollapseInitialized] = useState(false);
  const [accountSettingsFetched, setAccountSettingsFetched] = useState(false);
  const [allowedServicesBlockCollapsed, setAllowedServicesBlockCollapsed] = useState(false);
  
  // Allowed Regions editing state
  const [editingAllowedRegions, setEditingAllowedRegions] = useState(false);
  const [savingAllowedRegions, setSavingAllowedRegions] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [showDisableUsEast1Dialog, setShowDisableUsEast1Dialog] = useState(false);
  // Enhanced Allowed Services UI state
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [serviceSortBy, setServiceSortBy] = useState<'name' | 'resources' | 'status'>('name');
  const [serviceSortOrder, setServiceSortOrder] = useState<'asc' | 'desc'>('asc');
  const [stableSortOrder, setStableSortOrder] = useState<[string, string[]][]>([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  // Group services by status (enabled/disabled)
  const getGroupedServices = () => {
    const filtered = getFilteredServices();
    const enabled: [string, string[]][] = [];
    const disabled: [string, string[]][] = [];
    
    filtered.forEach(([service, resources]) => {
      const isEnabled = isServiceAllAllowed(service) || resources.some(r => isResourceAllowed(service, r));
      if (isEnabled) {
        enabled.push([service, resources]);
      } else {
        disabled.push([service, resources]);
      }
    });
    
    // Sort services within each group
    enabled.sort(([a], [b]) => a.localeCompare(b));
    disabled.sort(([a], [b]) => a.localeCompare(b));
    
    return {
      'Enabled Services': enabled,
      'Disabled Services': disabled
    };
  };

  // Update URL when tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    navigate(`/settings?tab=${newValue}`);
    
    // Load scan settings when System Settings tab is selected
    if (newValue === TAB_NAMES.SYSTEM_SETTINGS) {
      loadScanSettings();
      loadScanEvents();
    }
    // Load account settings data when Account Settings tab is selected
    if (newValue === TAB_NAMES.ACCOUNT_SETTINGS) {
      initAccountSettingsData();
    }
  };

  // Update tab when URL changes

  // Dynamic timezone data with real-time offset calculation
  const getTimezones = () => {
    const timezoneValues = [
      'UTC',
      // Africa
      'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Bamako',
      'Africa/Bangui', 'Africa/Banjul', 'Africa/Blantyre', 'Africa/Brazzaville', 'Africa/Bujumbura',
      'Africa/Cairo', 'Africa/Casablanca', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar_es_Salaam',
      'Africa/Djibouti', 'Africa/Douala', 'Africa/Freetown', 'Africa/Gaborone', 'Africa/Harare',
      'Africa/Johannesburg', 'Africa/Juba', 'Africa/Kampala', 'Africa/Khartoum', 'Africa/Kigali',
      'Africa/Kinshasa', 'Africa/Lagos', 'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda',
      'Africa/Lubumbashi', 'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maputo', 'Africa/Mogadishu',
      'Africa/Monrovia', 'Africa/Nairobi', 'Africa/Ndjamena', 'Africa/Niamey', 'Africa/Nouakchott',
      'Africa/Ouagadougou', 'Africa/Porto-Novo', 'Africa/Sao_Tome', 'Africa/Tripoli', 'Africa/Tunis',
      'Africa/Windhoek',
      // Americas
      'America/Adak', 'America/Anchorage', 'America/Chicago', 'America/Denver', 'America/Edmonton',
      'America/Halifax', 'America/Indianapolis', 'America/Los_Angeles', 'America/Mexico_City',
      'America/New_York', 'America/Phoenix', 'America/Regina', 'America/Sao_Paulo', 'America/Toronto',
      'America/Vancouver', 'America/Winnipeg',
      // Asia
      'Asia/Almaty', 'Asia/Amman', 'Asia/Ashgabat', 'Asia/Baghdad', 'Asia/Baku', 'Asia/Bangkok',
      'Asia/Beirut', 'Asia/Dhaka', 'Asia/Dubai', 'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong', 'Asia/Jakarta',
      'Asia/Jerusalem', 'Asia/Kabul', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Kuala_Lumpur',
      'Asia/Manila', 'Asia/Muscat', 'Asia/Novosibirsk', 'Asia/Qatar', 'Asia/Riyadh', 'Asia/Seoul',
      'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tashkent', 'Asia/Tehran', 'Asia/Tokyo',
      'Asia/Vladivostok', 'Asia/Yekaterinburg',
      // Europe
      'Europe/Amsterdam', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Bratislava',
      'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest', 'Europe/Copenhagen', 'Europe/Dublin',
      'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kiev', 'Europe/Lisbon', 'Europe/London',
      'Europe/Madrid', 'Europe/Minsk', 'Europe/Moscow', 'Europe/Oslo', 'Europe/Paris',
      'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/Stockholm', 'Europe/Tallinn',
      'Europe/Vienna', 'Europe/Vilnius', 'Europe/Warsaw', 'Europe/Zurich',
      // Pacific & Oceania
      'Pacific/Auckland', 'Pacific/Chatham', 'Pacific/Easter', 'Pacific/Fiji', 'Pacific/Guadalcanal',
      'Pacific/Guam', 'Pacific/Honolulu', 'Pacific/Kiritimati', 'Pacific/Kosrae', 'Pacific/Kwajalein',
      'Pacific/Majuro', 'Pacific/Marquesas', 'Pacific/Midway', 'Pacific/Nauru', 'Pacific/Niue',
      'Pacific/Norfolk', 'Pacific/Noumea', 'Pacific/Pago_Pago', 'Pacific/Palau', 'Pacific/Pitcairn',
      'Pacific/Ponape', 'Pacific/Port_Moresby', 'Pacific/Rarotonga', 'Pacific/Tahiti', 'Pacific/Tarawa',
      'Pacific/Tongatapu', 'Pacific/Truk', 'Pacific/Wake', 'Pacific/Wallis',
      // Australia
      'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin', 'Australia/Hobart',
      'Australia/Lord_Howe', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney'
    ];

    return timezoneValues.map(tz => {
      try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'long',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        const parts = formatter.formatToParts(now);
        const timezoneName = parts.find(part => part.type === 'timeZoneName')?.value || tz;
        
        // Calculate current offset dynamically using Intl.DateTimeFormat
        const date = new Date();
        const utcString = date.toLocaleString('en-US', { timeZone: 'UTC' });
        const targetString = date.toLocaleString('en-US', { timeZone: tz });
        
        const utcDate = new Date(utcString);
        const targetDate = new Date(targetString);
        
        const offsetMs = targetDate.getTime() - utcDate.getTime();
        const offsetHours = offsetMs / (1000 * 60 * 60);
        
        return {
          value: tz,
          label: `${tz} (${timezoneName})`,
          offset: Math.round(offsetHours * 100) / 100 // Round to 2 decimal places
        };
      } catch (error) {
        // Fallback for invalid timezones
        return {
          value: tz,
          label: `${tz} (Unknown)`,
          offset: 0
        };
      }
    });
  };

  const timezones = getTimezones();

  // Days of week
  const daysOfWeek = [
    { value: 'SUN', label: 'Sunday' },
    { value: 'MON', label: 'Monday' },
    { value: 'TUE', label: 'Tuesday' },
    { value: 'WED', label: 'Wednesday' },
    { value: 'THU', label: 'Thursday' },
    { value: 'FRI', label: 'Friday' },
    { value: 'SAT', label: 'Saturday' },
  ];

  // Scan Settings Functions
  const loadScanSettings = useCallback(async () => {
    if (loadingScanSettings) return;
    
    setLoadingScanSettings(true);
    try {
      const response = await tagopsService.getScanSettings();
      console.log('Scan settings response:', response);
      
      if (response?.data) {
        setScanSettings(response.data);
        
        // Parse the days from the cron expression and update selectedDays
        const cronParts = parseCronExpression(response.data.cron);
        const daysFromCron = parseDaysFromCron(cronParts.dayOfWeek);
        setSelectedDays(daysFromCron);
        
        // Initialize validation state
        setTimeValidation({
          hour: validateHour(cronParts.hour),
          minute: validateMinute(cronParts.minute)
        });
      }
    } catch (error: any) {
      console.error('Error loading scan settings:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to load scan settings'));
    } finally {
      setLoadingScanSettings(false);
    }
  }, [showNotification]);

  const loadScanEvents = useCallback(async () => {
    if (scanEventsLoadInFlight.current) return;
    scanEventsLoadInFlight.current = true;
    setLoadingScanEvents(true);
    try {
      const response = await tagopsService.getScanEvents();
      const limits = response?.data?.scan_limits;
      setScanLimitEvents(Array.isArray(limits) ? limits : []);
    } catch (error: any) {
      console.error('Error loading scan events:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to load scan events'));
      setScanLimitEvents([]);
    } finally {
      setLoadingScanEvents(false);
      scanEventsLoadInFlight.current = false;
    }
  }, [showNotification]);

  const acknowledgeScanLimit = async (accountId: string, resource: string) => {
    const key = `${accountId}::${resource}`;
    setAcknowledgingScanLimitKey(key);
    try {
      await tagopsService.acknowledgeScanEvent({ accountId, resource });
      showNotification(
        createSuccessNotification('Acknowledged', 'Scan limit notice was cleared.')
      );
      await loadScanEvents();
    } catch (error: any) {
      console.error('Error acknowledging scan event:', error);
      showNotification(
        createErrorNotification(
          error,
          undefined,
          error.response?.data?.message || error.message || 'Failed to acknowledge scan event'
        )
      );
    } finally {
      setAcknowledgingScanLimitKey(null);
    }
  };

  const parseCronExpression = (cron: string) => {
    const parts = cron.split(' ');
    if (parts.length >= 6) {
      const dayOfWeek = parts[4];
      
      return {
        minute: parts[0],
        hour: parts[1],
        day: parts[2],
        month: parts[3],
        dayOfWeek: dayOfWeek,
        year: parts[5]
      };
    }
    return { minute: '0', hour: '0', day: '*', month: '*', dayOfWeek: '?', year: '*' };
  };

  const parseDaysFromCron = (dayOfWeek: string): string[] => {
    if (dayOfWeek === '?') {
      return []; // All days (empty selection means all days)
    } else {
      return dayOfWeek.split(',');
    }
  };

  const buildCronExpression = (minute: string, hour: string, selectedDaysArray: string[]) => {
    let day = '*';
    let dayOfWeek = '?';
    
    // If no days selected or all days selected (7 days), use ? for every day
    if (selectedDaysArray.length === 0 || selectedDaysArray.length === 7) {
      day = '*';
      dayOfWeek = '?';
    } else {
      // If specific days selected, use ? for day and list days for dayOfWeek
      day = '?';
      dayOfWeek = selectedDaysArray.join(',');
    }
    
    return `${minute} ${hour} ${day} * ${dayOfWeek} *`;
  };

  const calculateTimezoneOffset = (timezone: string): number => {
    const tz = timezones.find(t => t.value === timezone);
    return tz ? tz.offset : 0;
  };

  // Validation functions
  const validateHour = (value: string): { isValid: boolean; error: string } => {
    if (value === '' || value === undefined || value === null) {
      return { isValid: false, error: 'Hour is required' };
    }
    
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      return { isValid: false, error: 'Hour must be a number' };
    }
    
    if (numValue < 0 || numValue > 23) {
      return { isValid: false, error: 'Hour must be between 0 and 23' };
    }
    
    return { isValid: true, error: '' };
  };

  const validateMinute = (value: string): { isValid: boolean; error: string } => {
    if (value === '' || value === undefined || value === null) {
      return { isValid: false, error: 'Minute is required' };
    }
    
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      return { isValid: false, error: 'Minute must be a number' };
    }
    
    if (numValue < 0 || numValue > 59) {
      return { isValid: false, error: 'Minute must be between 0 and 59' };
    }
    
    return { isValid: true, error: '' };
  };

  const isFormValid = (): boolean => {
    const cronParts = parseCronExpression(scanSettings.cron);
    const hourValidation = validateHour(cronParts.hour);
    const minuteValidation = validateMinute(cronParts.minute);
    
    return hourValidation.isValid && minuteValidation.isValid;
  };


  const saveScanSettings = async () => {
    setSavingScanSettings(true);
    try {
      const offset = calculateTimezoneOffset(scanSettings.timezone);
      
      const updateData = {
        status: scanSettings.status,
        cron: scanSettings.cron,
        timezone: scanSettings.timezone,
        offset: offset
      };

      await tagopsService.updateScanSettings(updateData);
      
      showNotification(createSuccessNotification(
        'Scan settings saved successfully',
        'The scan configuration has been updated.'
      ));
      
      setEditingScanSettings(false);
      setHasChanges(false);
      setTimezoneSearch('');
      // Reload settings to get the updated UTC cron
      await loadScanSettings();
      
    } catch (error: any) {
      console.error('Error saving scan settings:', error);
      showNotification(createErrorNotification(
        error,
        undefined,
        error.response?.data?.message || error.message || 'Failed to save scan settings'
      ));
    } finally {
      setSavingScanSettings(false);
    }
  };

  const cancelEditScanSettings = () => {
    setEditingScanSettings(false);
    setHasChanges(false);
    setTimezoneSearch('');
    // Reload settings to reset any changes
    loadScanSettings();
  };

  // ----- Account Settings: Allowed Services helpers -----
  const initAccountSettingsData = useCallback(async () => {
    if (!loadingAccounts && accounts.length === 0) {
      loadAccountsList();
    }
    if (!loadingSupportedServices && Object.keys(supportedServices).length === 0) {
      loadSupportedServices();
    }
  }, []);

  const loadAccountsList = async () => {
    setLoadingAccounts(true);
    try {
      const resp: any = await tagopsService.getAccounts();
      const list: AccountItem[] = (resp?.accounts || []).map((a: any) => ({ 
        accountId: a.accountId, 
        account_name: a.account_name, 
        regions: a.regions,
        allowed_regions: a.allowed_regions,
        allowerd_regions: a.allowerd_regions
      }));
      setAccounts(list);
      // If no selection yet and there are accounts, set first
      if (!selectedAccountId && list.length > 0) {
        setSelectedAccountId(list[0].accountId);
        await loadAccountServiceSettings(list[0].accountId);
      }
    } catch (error: any) {
      showNotification(createErrorNotification(error, loadAccountsList));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadSupportedServices = async () => {
    setLoadingSupportedServices(true);
    try {
      const resp = await tagopsService.getSupportedServices();
      const deduped: Record<string, string[]> = {};
      Object.entries(resp || {}).forEach(([svc, resources]) => {
        const safeArray = Array.isArray(resources) ? resources : [];
        const unique = Array.from(new Set(safeArray)).sort();
        deduped[svc] = unique;
      });
      setSupportedServices(deduped);
    } catch (error: any) {
      showNotification(createErrorNotification(error, loadSupportedServices));
    } finally {
      setLoadingSupportedServices(false);
    }
  };

  const cleanupInvalidDeniedResources = (allowed: string[], denied: string[]) => {
    // Remove denied resources for services that don't have "All" selected
    const cleanedDenied = denied.filter(deniedItem => {
      const [service] = deniedItem.split(':');
      return allowed.includes(`${service}:*`);
    });
    
    return cleanedDenied;
  };

  const loadAccountServiceSettings = async (accountId: string) => {
    setLoadingAccountServiceSettings(true);
    try {
      const resp: any = await tagopsService.getAccountServiceSettings(accountId);
      
      // Handle nested array structure - flatten if needed
      let allowed = resp?.allowed_resources || [];
      if (Array.isArray(allowed) && allowed.length > 0 && Array.isArray(allowed[0])) {
        // If it's a nested array, flatten it
        allowed = allowed[0];
      }
      allowed = Array.isArray(allowed) ? allowed : [];
      
      let denied = resp?.denied_resources || [];
      if (Array.isArray(denied) && denied.length > 0 && Array.isArray(denied[0])) {
        // If it's a nested array, flatten it
        denied = denied[0];
      }
      denied = Array.isArray(denied) ? denied : [];
      
      // Clean up any invalid denied resources
      const cleanedDenied = cleanupInvalidDeniedResources(allowed, denied);
      
      setAccountServiceSettings({ allowed_resources: allowed, denied_resources: cleanedDenied });
    } catch (error: any) {
      showNotification(createErrorNotification(error));
      setAccountServiceSettings({ allowed_resources: [], denied_resources: [] });
    } finally {
      setLoadingAccountServiceSettings(false);
      setAccountSettingsFetched(true);
    }
  };

  const handleSelectAccount = async (accountId: string) => {
    setSelectedAccountId(accountId);
    await loadAccountServiceSettings(accountId);
    setEditingAllowedServices(false);
    // Reset collapsed services when switching accounts
    setCollapsedServices(new Set());
    setCollapseInitialized(false);
    setAccountSettingsFetched(false);
  };

  const isServiceAllAllowed = (service: string): boolean => {
    return accountServiceSettings.allowed_resources.includes(`${service}:*`);
  };

  const isResourceAllowed = (service: string, resource: string): boolean => {
    return accountServiceSettings.allowed_resources.includes(`${service}:${resource}`);
  };

  const isResourceDenied = (service: string, resource: string): boolean => {
    return accountServiceSettings.denied_resources.includes(`${service}:${resource}`);
  };

  const toggleServiceAllAllow = (service: string, checked: boolean) => {
    // If we're sorted by status and this is the first interaction, capture the current sort order
    if (serviceSortBy === 'status' && !isUserInteracting) {
      setIsUserInteracting(true);
      setStableSortOrder(getFilteredServices());
    }
    
    setAccountServiceSettings(prev => {
      let allowed = [...prev.allowed_resources];
      let denied = [...prev.denied_resources];
      const svcAll = `${service}:*`;
      const serviceResources = supportedServices[service] || [];
      // Remove all per-resource allowed entries for this service to avoid redundancy
      allowed = allowed.filter(item => typeof item === 'string' && (!item.startsWith(`${service}:`) || item === svcAll));
      // Ensure svcAll presence based on checked
      if (checked) {
        if (!allowed.includes(svcAll)) allowed.push(svcAll);
        
        // If service has only one resource, remove any denied entries since they're meaningless
        if (serviceResources.length === 1) {
          denied = denied.filter(item => typeof item === 'string' && !item.startsWith(`${service}:`));
        }
      } else {
        allowed = allowed.filter(item => item !== svcAll);
        // When unselecting All, clear denied for that service since it's no longer relevant
        denied = denied.filter(item => typeof item === 'string' && !item.startsWith(`${service}:`));
      }
      return { allowed_resources: allowed, denied_resources: denied };
    });
    
    // Reset interaction state after a delay to allow for normal sorting to resume
    if (isUserInteracting && serviceSortBy === 'status') {
      setTimeout(() => {
        setIsUserInteracting(false);
        setStableSortOrder([]);
      }, 2000); // 2 second delay to allow user to finish their interaction
    }
  };

  const toggleResourceAllow = (service: string, resource: string, checked: boolean) => {
    setAccountServiceSettings(prev => {
      let allowed = [...prev.allowed_resources];
      let denied = [...prev.denied_resources];
      const svcAll = `${service}:*`;
      const key = `${service}:${resource}`;
      
      // If enabling a specific resource, ensure svcAll is not present
      if (checked) {
        allowed = allowed.filter(item => item !== svcAll);
        if (!allowed.includes(key)) allowed.push(key);
        
        // Check if all resources for this service are now selected
        const serviceResources = supportedServices[service] || [];
        const selectedResources = allowed.filter(item => typeof item === 'string' && item.startsWith(`${service}:`) && item !== svcAll);
        if (selectedResources.length === serviceResources.length) {
          // All resources selected, switch to "All"
          allowed = allowed.filter(item => typeof item === 'string' && !item.startsWith(`${service}:`));
          allowed.push(svcAll);
        }
        
        // When switching from "All" to individual resources, clear denied resources for this service
        // since denied resources are only meaningful when "All" is selected
        if (prev.allowed_resources.includes(svcAll)) {
          denied = denied.filter(item => typeof item === 'string' && !item.startsWith(`${service}:`));
        }
      } else {
        allowed = allowed.filter(item => item !== key);
      }
      
      return { allowed_resources: allowed, denied_resources: denied };
    });
  };

  const toggleResourceDeny = (service: string, resource: string, checked: boolean) => {
    setAccountServiceSettings(prev => {
      let denied = [...prev.denied_resources];
      const key = `${service}:${resource}`;
      const svcAll = `${service}:*`;
      
      // Only allow denied resources when "All" is selected for that service
      // Denied resources are meaningless when individual resources are selected
      if (checked) {
        if (!prev.allowed_resources.includes(svcAll)) {
          // If "All" is not selected, don't allow denied resources
          return prev;
        }
        if (!denied.includes(key)) denied.push(key);
      } else {
        denied = denied.filter(item => item !== key);
      }
      return { ...prev, denied_resources: denied };
    });
  };

  const saveAllowedServices = async () => {
    if (!selectedAccountId) return;
    setSavingAllowedServices(true);
    try {
      // Clean up any invalid denied resources before saving
      const cleanedDenied = cleanupInvalidDeniedResources(
        accountServiceSettings.allowed_resources,
        accountServiceSettings.denied_resources
      );
      
      await tagopsService.updateAccountServiceSettings({
        accountId: selectedAccountId,
        data: {
          allowed_resources: accountServiceSettings.allowed_resources,
          denied_resources: cleanedDenied,
        },
      });
      showNotification(createSuccessNotification('Success', 'Service settings updated successfully'));
      setEditingAllowedServices(false);
      await loadAccountServiceSettings(selectedAccountId);
    } catch (error: any) {
      showNotification(createErrorNotification(error));
    } finally {
      setSavingAllowedServices(false);
    }
  };

  const cancelEditAllowedServices = async () => {
    setEditingAllowedServices(false);
    if (selectedAccountId) {
      await loadAccountServiceSettings(selectedAccountId);
    }
  };

  // Select ALL services functionality
  const selectAllServices = () => {
    const allServices = Object.keys(supportedServices);
    const allAllowedResources = allServices.map(service => `${service}:*`);
    setAccountServiceSettings(prev => ({
      ...prev,
      allowed_resources: allAllowedResources,
      denied_resources: []
    }));
  };

  // Unselect ALL services functionality
  const unselectAllServices = () => {
    setAccountServiceSettings(prev => ({
      ...prev,
      allowed_resources: [],
      denied_resources: []
    }));
  };

  // Filter and sort services based on search term and sorting criteria
  const getFilteredServices = () => {
    let filtered: [string, string[]][] = [];
    
    // Apply search term filter
    if (!serviceSearchTerm.trim()) {
      filtered = Object.entries(supportedServices);
    } else {
      const searchLower = serviceSearchTerm.toLowerCase();
      
      Object.entries(supportedServices).forEach(([serviceName, resources]) => {
        // Check if service name matches search term
        if (serviceName.toLowerCase().includes(searchLower)) {
          filtered.push([serviceName, resources]);
        } else {
          // Check if any resource in the service matches search term
          const matchingResources = resources.filter(resource => 
            resource.toLowerCase().includes(searchLower)
          );
          if (matchingResources.length > 0) {
            filtered.push([serviceName, resources]); // Show all resources for the service if any match
          }
        }
      });
    }
    
    // If user is interacting and we have a stable sort order, use it to maintain position
    if (isUserInteracting && stableSortOrder.length > 0 && serviceSortBy === 'status') {
      // Create a map of current services for quick lookup
      const currentServicesMap = new Map(filtered.map(([service, resources]) => [service, resources]));
      
      // Use the stable sort order, but only include services that are still in the filtered results
      const stableFiltered = stableSortOrder
        .filter(([service]) => currentServicesMap.has(service))
        .map(([service]) => [service, currentServicesMap.get(service)!] as [string, string[]]);
      
      // Add any new services that weren't in the stable order (shouldn't happen, but safety)
      const stableServiceNames = new Set(stableFiltered.map(([service]) => service));
      const newServices = filtered.filter(([service]) => !stableServiceNames.has(service));
      
      return [...stableFiltered, ...newServices];
    }
    
    // Sort the filtered results normally
    filtered.sort(([serviceA, resourcesA], [serviceB, resourcesB]) => {
      let aValue: any;
      let bValue: any;
      
      switch (serviceSortBy) {
        case 'name':
          aValue = serviceA.toLowerCase();
          bValue = serviceB.toLowerCase();
          break;
        case 'resources':
          aValue = resourcesA.length;
          bValue = resourcesB.length;
          break;
        case 'status':
          const aAllowed = isServiceAllAllowed(serviceA) || resourcesA.some(r => isResourceAllowed(serviceA, r));
          const bAllowed = isServiceAllAllowed(serviceB) || resourcesB.some(r => isResourceAllowed(serviceB, r));
          aValue = aAllowed ? 1 : 0;
          bValue = bAllowed ? 1 : 0;
          break;
        default:
          aValue = serviceA.toLowerCase();
          bValue = serviceB.toLowerCase();
      }
      
      if (serviceSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  };

  const getSelectedAccountRegions = (): string[] => {
    const acc = accounts.find(a => a.accountId === selectedAccountId);
    if (!acc) return [];
    
    // Handle both correct field name and the typo in the API response
    const allowedRegionsField = acc.allowed_regions || acc.allowerd_regions;
    if (!allowedRegionsField) return [];
    
    // Check if it's already an array (from API response) or a JSON string (from database)
    if (Array.isArray(allowedRegionsField)) {
      return allowedRegionsField;
    }
    
    try {
      const parsed = JSON.parse(allowedRegionsField);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Failed to parse account allowed_regions', e);
      return [];
    }
  };

  // Get available regions (onboarded regions) for the selected account
  const getAvailableRegions = (): string[] => {
    const acc = accounts.find(a => a.accountId === selectedAccountId);
    if (!acc || !acc.regions) return [];
    
    // Check if it's already an array (from API response) or a JSON string (from database)
    if (Array.isArray(acc.regions)) {
      return acc.regions;
    }
    
    try {
      const parsed = JSON.parse(acc.regions);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Failed to parse account regions', e);
      return [];
    }
  };

  // Handle edit allowed regions
  const handleEditAllowedRegions = () => {
    const currentRegions = getSelectedAccountRegions();
    const available = getAvailableRegions();
    setSelectedRegions([...currentRegions]);
    setAvailableRegions([...available]);
    setEditingAllowedRegions(true);
  };

  // Handle cancel editing allowed regions
  const handleCancelEditAllowedRegions = () => {
    setEditingAllowedRegions(false);
    setSelectedRegions([]);
    setAvailableRegions([]);
  };

  // Handle save allowed regions
  const handleSaveAllowedRegions = async () => {
    if (!selectedAccountId) return;
    
    setSavingAllowedRegions(true);
    try {
      await tagopsService.updateAccountAllowedRegions({
        accountId: selectedAccountId,
        allowed_regions: selectedRegions
      });
      
      // Update local state
      setAccounts(prev => prev.map(acc => 
        acc.accountId === selectedAccountId 
          ? { ...acc, allowed_regions: JSON.stringify(selectedRegions) }
          : acc
      ));
      
      setEditingAllowedRegions(false);
      setSelectedRegions([]);
      setAvailableRegions([]);
      
      showNotification(createSuccessNotification(
        'Success',
        'Allowed regions updated successfully'
      ));
    } catch (error: any) {
      console.error('Error updating allowed regions:', error);
      showNotification(createErrorNotification(
        error,
        undefined,
        error.response?.data?.message || error.message || 'Failed to update allowed regions'
      ));
    } finally {
      setSavingAllowedRegions(false);
    }
  };

  // Handle region selection toggle
  const handleRegionToggle = (region: string) => {
    setSelectedRegions(prev => {
      const isDisabling = prev.includes(region);

      // us-east-1 safety confirmation before disabling
      if (region === 'us-east-1' && isDisabling) {
        setShowDisableUsEast1Dialog(true);
        return prev; // do not change selection until confirmed
      }

      return isDisabling ? prev.filter(r => r !== region) : [...prev, region];
    });
  };


  // Column sorting functions
  const handleColumnSort = (column: 'name' | 'resources' | 'status') => {
    if (serviceSortBy === column) {
      // If clicking the same column, toggle sort order
      setServiceSortOrder(serviceSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as the new sort column with ascending order
      setServiceSortBy(column);
      setServiceSortOrder('asc');
    }
    
    // Reset interaction state when clicking column headers
    setIsUserInteracting(false);
    setStableSortOrder([]);
  };

  // Compute default collapsed services based on configuration state
  const computeDefaultCollapsedServices = (
    servicesMap: Record<string, string[]>,
    settings: { allowed_resources: string[]; denied_resources: string[] }
  ): Set<string> => {
    const result = new Set<string>();
    const allowed = settings.allowed_resources || [];
    const denied = settings.denied_resources || [];
    
    Object.keys(servicesMap).forEach((service) => {
      const hasAll = allowed.includes(`${service}:*`);
      const hasSpecificAllowed = allowed.some(a => typeof a === 'string' && a.startsWith(`${service}:`) && a !== `${service}:*`);
      const hasDenied = denied.some(d => typeof d === 'string' && d.startsWith(`${service}:`));
      
      // Collapse when: no allowed at all OR All is selected
      // Expand when: partial allowed OR any denied exists
      if ((!hasAll && !hasSpecificAllowed) || hasAll) {
        if ((hasSpecificAllowed && !hasAll) || hasDenied) {
          // expand, do nothing
        } else {
          result.add(service);
        }
      }
    });
    return result;
  };

  // Ensure default collapsed state is computed once when both datasets are ready
  useEffect(() => {
    if (!collapseInitialized && selectedAccountId && Object.keys(supportedServices).length > 0 && accountSettingsFetched) {
      const next = computeDefaultCollapsedServices(supportedServices, accountServiceSettings);
      setCollapsedServices(next);
      setCollapseInitialized(true);
    }
  }, [collapseInitialized, selectedAccountId, supportedServices, accountServiceSettings, accountSettingsFetched]);

  // Service visibility logic (list which services render at all)

  const toggleServiceCollapsed = (service: string) => {
    setCollapsedServices(prev => {
      const next = new Set(prev);
      if (next.has(service)) next.delete(service); else next.add(service);
      return next;
    });
  };



  const loadUsers = async () => {
    setLoading(true);
    try {
      // Load all users - filtering is done client-side like Inventory page
      const result = await tagopsService.listUsers({});
      // The service method returns the unwrapped data directly
      if (result && result.users) {
        // Transform the API response to match our User interface
        const transformedUsers = result.users.map((user: any) => {
          return {
            ...user,
            // Provide default values for missing fields
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            userName: user.userName || '',
            // Map status field correctly - the API returns status directly
            status: user.status || 'UNKNOWN',
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: user.updatedAt || new Date().toISOString(),
            // The backend already provides the role field, no conversion needed
            role: user.role || 'ReadOnly'
          };
        });
        setUsers(transformedUsers);
      } else {
        showNotification(createErrorNotification('Failed to load users'));
      }
    } catch (error) {
      showNotification(createErrorNotification(error, loadUsers));
    } finally {
      setLoading(false);
    }
  };

  // Sync tab value from URL - always keep state in sync with URL
  useEffect(() => {
    if (currentTabFromUrl !== tabValue) {
      setTabValue(currentTabFromUrl);
    }
  }, [currentTabFromUrl, tabValue]);

  // Load data based on active tab - use currentTabFromUrl to ensure it's always in sync with URL
  useEffect(() => {
    // Load scan settings if System Settings tab is active
    if (currentTabFromUrl === TAB_NAMES.SYSTEM_SETTINGS) {
      loadScanSettings();
      loadScanEvents();
    }
    // Load account settings if Account Settings tab is active
    if (currentTabFromUrl === TAB_NAMES.ACCOUNT_SETTINGS) {
      initAccountSettingsData();
    }
    // Load users if User Management tab is active
    if (currentTabFromUrl === TAB_NAMES.USER_MANAGEMENT) {
      loadUsers();
    }
  }, [currentTabFromUrl, loadScanSettings, loadScanEvents, initAccountSettingsData]);

  // Load users only when tab changes - filtering is done client-side
  useEffect(() => {
    if (currentTabFromUrl === TAB_NAMES.USER_MANAGEMENT) {
      loadUsers();
    }
  }, [currentTabFromUrl]);

  const handleInviteUser = async () => {
    try {
      // Call the actual API
      const result = await tagopsService.addUser(newUser);
      
      if (result && result.userId) {
        const user: User = {
          userName: result.userId,
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          role: newUser.role,
          status: 'ENABLED', // Users are enabled by default
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tenantId: 'tenant-1'
        };

        setUsers([...users, user]);
        setShowInviteDialog(false);
        setNewUser({ email: '', firstName: '', lastName: '', role: 'PowerUser' });
        showNotification(createSuccessNotification('Success', result.message || 'User invited successfully'));
      } else {
        showNotification(createErrorNotification('Failed to invite user'));
      }
    } catch (error) {
      showNotification(createErrorNotification(error));
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    // For administrators editing their own account, only allow first/last name updates
    if (isEditingOwnAdminAccount(selectedUser)) {
      // Create a restricted update with only allowed fields
      const restrictedUpdates = [{
        userId: selectedUser.userName,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName
        // Note: role, status, and email are excluded for self-editing administrators
      }];
      
      try {
        setUpdatingUsers(prev => new Set(prev).add(selectedUser.userName));
        
        const result = await tagopsService.updateUsers(restrictedUpdates);
        
        if (result && result.updatedUsers) {
          setUsers(users.map(user => 
            user.userName === selectedUser.userName ? selectedUser : user
          ));
          setShowEditDialog(false);
          setSelectedUser(null);
          showNotification(createSuccessNotification('Success', 'Name updated successfully (role, status, and email cannot be changed for your own account)'));
        }
      } catch (error) {
        showNotification(createErrorNotification(error));
      } finally {
        setUpdatingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(selectedUser.userName);
          return newSet;
        });
      }
      return;
    }

    try {
      setUpdatingUsers(prev => new Set(prev).add(selectedUser.userName));
      
      // Call the actual API
      const updates = [{
        userId: selectedUser.userName,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        role: selectedUser.role,
        status: selectedUser.status as 'ENABLED' | 'DISABLED'
      }];
      
      const result = await tagopsService.updateUsers(updates);
      
      if (result && result.updatedUsers) {
        setUsers(users.map(user => 
          user.userName === selectedUser.userName ? selectedUser : user
        ));
        setShowEditDialog(false);
        setSelectedUser(null);
        showNotification(createSuccessNotification('Success', result.message || 'User updated successfully'));
      }
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedUser.userName);
        return newSet;
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const user = users.find(u => u.userName === userId);
    if (!user) return;
    
    // Prevent administrators from deleting themselves
    if (isEditingOwnAdminAccount(user)) {
      showNotification(createErrorNotification('Administrators cannot delete their own account'));
      return;
    }
    
    try {
      setUpdatingUsers(prev => new Set(prev).add(userId));
      
      // Call the actual API
      const result = await tagopsService.deleteUsers([userId]);
      
      if (result && result.deletedUsers) {
        setUsers(users.filter(user => user.userName !== userId));
        showNotification(createSuccessNotification('Success', result.message || 'User deleted successfully'));
      }
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser({ ...user });
    setShowEditDialog(true);
  };

  // Enhanced User Management Functions - client-side filtering like Inventory
  const filteredAndSortedUsers = () => {
    let filtered = users.filter(user => {
      // Enhanced search - search across all relevant fields like Inventory does
      if (userFilter) {
        const searchLower = userFilter.toLowerCase();
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const userName = (user.userName || '').toLowerCase();
        
        const matchesFilter = fullName.includes(searchLower) ||
                             email.includes(searchLower) ||
                             userName.includes(searchLower) ||
                             // Also search individual name parts
                             (user.firstName || '').toLowerCase().includes(searchLower) ||
                             (user.lastName || '').toLowerCase().includes(searchLower);
        
        if (!matchesFilter) {
          return false;
        }
      }
      
      const matchesStatus = userStatusFilter === 'all' || (user.status || '').toUpperCase() === userStatusFilter;
      const matchesRole = userRoleFilter === 'all' || (user.role || 'Unknown') === userRoleFilter;
      
      return matchesStatus && matchesRole;
    });

    // Sort users
    filtered.sort((a, b) => {
      let aValue: any = a[userSortBy];
      let bValue: any = b[userSortBy];
      
      if (userSortBy === 'createdAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      if (userSortBy === 'firstName') {
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
      }
      
      if (userSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleUserSelection = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAllUsers = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(users.map(u => u.userName)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleMassEdit = async () => {
    if (selectedUsers.size === 0 || !massEditValue) return;

    // Check if current user is trying to modify their own administrator account
    const currentUserInSelection = Array.from(selectedUsers).some(userId => {
      const user = users.find(u => u.userName === userId);
      return user && isEditingOwnAdminAccount(user);
    });

    if (currentUserInSelection) {
      showNotification(createErrorNotification('You cannot modify your own administrator account through mass edit'));
      return;
    }

    try {
      const updates = Array.from(selectedUsers).map(userId => {
        const user = users.find(u => u.userName === userId);
        if (!user) return null;
        
        if (massEditAction === 'role') {
          return {
            userId: user.userName,
            role: massEditValue as 'Administrator' | 'PowerUser' | 'ReadOnly'
          };
        } else if (massEditAction === 'status') {
          return {
            userId: user.userName,
            status: massEditValue as 'ENABLED' | 'DISABLED'
          };
        }
        return null;
      }).filter((update): update is NonNullable<typeof update> => update !== null);

      if (updates.length > 0) {
        // Set loading state for all selected users
        setUpdatingUsers(prev => new Set([...Array.from(prev), ...Array.from(selectedUsers)]));
        
        const result = await tagopsService.updateUsers(updates);
        
        if (result && result.updatedUsers) {
          // Update local state
          const updatedUsers = users.map(user => {
            if (selectedUsers.has(user.userName)) {
              if (massEditAction === 'role') {
                return { ...user, role: massEditValue as 'Administrator' | 'PowerUser' | 'ReadOnly' };
              } else if (massEditAction === 'status') {
                return { ...user, status: massEditValue as 'ENABLED' | 'DISABLED' };
              }
            }
            return user;
          }) as User[];

          setUsers(updatedUsers);
          showNotification(createSuccessNotification('Success', result.message || `Updated ${updates.length} users`));
        }
        
        // Clear loading state for all selected users
        setUpdatingUsers(prev => {
          const newSet = new Set(prev);
          Array.from(selectedUsers).forEach(userId => newSet.delete(userId));
          return newSet;
        });
      }
      
      setSelectedUsers(new Set());
      setShowMassEditDialog(false);
      setMassEditValue('');
    } catch (error) {
      showNotification(createErrorNotification(error));
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    const user = users.find(u => u.userName === userId);
    if (!user) return;
    
    // Prevent administrators from toggling their own status
    if (isEditingOwnAdminAccount(user)) {
      showNotification(createErrorNotification('Administrators cannot change their own status'));
      return;
    }
    
    try {
      setUpdatingUsers(prev => new Set(prev).add(userId));
      
      const newStatus: 'ENABLED' | 'DISABLED' = user.status.toUpperCase() === 'ENABLED' ? 'DISABLED' : 'ENABLED';
      const updates = [{
        userId: userId,
        status: newStatus
      }];
      
      const result = await tagopsService.updateUsers(updates);
      
      if (result && result.updatedUsers) {
        const updatedUsers = users.map(u => {
          if (u.userName === userId) {
            return { ...u, status: newStatus };
          }
          return u;
        }) as User[];
        setUsers(updatedUsers);
        showNotification(createSuccessNotification('Success', result.message || 'User status updated'));
      }
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };




  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Administrator':
        return 'error';
      case 'PowerUser':
        return 'primary';
      case 'ReadOnly':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'ENABLED':
        return 'success';
      case 'FORCE_CHANGE_PASSWORD':
        return 'warning';
      case 'DISABLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Administrator':
        return <AdminIcon />;
      case 'PowerUser':
        return <PersonIcon />;
      case 'ReadOnly':
        return <GroupIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const renderUserManagement = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">
              Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {users.length} user{users.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setUserFilter('');
                setUserStatusFilter('all');
                setUserRoleFilter('all');
                setSelectedUsers(new Set());
                // No need to reload - filtering is client-side
              }}
              disabled={loading}
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              onClick={loadUsers}
              disabled={loading}
            >
              Refresh
            </Button>
            {selectedUsers.size > 0 && (
              <PermissionButton
                permission="Administrator"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setShowMassEditDialog(true)}
                disabled={Array.from(selectedUsers).some(userId => updatingUsers.has(userId))}
              >
                Mass Edit ({selectedUsers.size})
              </PermissionButton>
            )}
            <PermissionButton
              permission="Administrator"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowInviteDialog(true)}
            >
              Invite User
            </PermissionButton>
          </Box>
        </Box>

        {/* Enhanced Filtering and Sorting */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={userFilter}
                onChange={(e) => {
                  setUserFilter(e.target.value);
                  // Search is handled client-side, no API call needed
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
                         <Grid item xs={12} md={2}>
               <FormControl fullWidth size="small">
                 <InputLabel>Status</InputLabel>
                 <Select
                   value={userStatusFilter}
                   label="Status"
                   onChange={(e) => {
                     setUserStatusFilter(e.target.value as any);
                     // Filtering is done client-side, no API call needed
                   }}
                 >
                   <MenuItem value="all">All Status</MenuItem>
                   <MenuItem value="ENABLED">Enabled</MenuItem>
                   <MenuItem value="DISABLED">Disabled</MenuItem>
                 </Select>
               </FormControl>
             </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={userRoleFilter}
                  label="Role"
                  onChange={(e) => {
                    setUserRoleFilter(e.target.value as any);
                    // Filtering is done client-side, no API call needed
                  }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="Administrator">Administrator</MenuItem>
                  <MenuItem value="PowerUser">Power User</MenuItem>
                  <MenuItem value="ReadOnly">Read Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                                     value={userSortBy}
                   label="Sort By"
                   onChange={(e) => {
                     setUserSortBy(e.target.value as any);
                     // Sorting is done client-side, no need to reload
                   }}
                >
                  <MenuItem value="firstName">Name</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="role">Role</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="createdAt">Created</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                size="small"
                                 onClick={() => {
                   setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                   // Sorting is done client-side, no need to reload
                 }}
                startIcon={userSortOrder === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              >
                {userSortOrder === 'asc' ? 'Asc' : 'Desc'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userFilter || userStatusFilter !== 'all' || userRoleFilter !== 'all' 
                ? 'Try adjusting your filters or clear them to see all users.' 
                : 'No users have been created yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.size === users.length && users.length > 0}
                      indeterminate={selectedUsers.size > 0 && selectedUsers.size < users.length}
                      onChange={(e) => handleSelectAllUsers(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedUsers().map((user) => (
                  <TableRow key={user.userName} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.has(user.userName)}
                        onChange={(e) => handleUserSelection(user.userName, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {(user.firstName || user.email || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role || 'Unknown')}
                        label={(user.role || 'Unknown').charAt(0).toUpperCase() + (user.role || 'Unknown').slice(1)}
                        color={getRoleColor(user.role || 'Unknown')}
                        size="small"
                        sx={
                          (user.role || 'Unknown') === 'ReadOnly'
                            ? {
                                bgcolor: theme.palette.mode === 'dark' 
                                  ? 'rgba(148, 163, 184, 0.2)' // grey.400 with opacity for dark mode
                                  : 'rgba(148, 163, 184, 0.15)', // grey.400 with opacity for light mode
                                color: theme.palette.mode === 'dark' 
                                  ? '#cbd5e1' // grey.600 for dark mode - lighter text
                                  : '#475569', // grey.700 for light mode - darker text
                                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.25)'}`,
                                '& .MuiChip-icon': {
                                  color: theme.palette.mode === 'dark' 
                                    ? '#cbd5e1' 
                                    : '#475569',
                                },
                              }
                            : undefined
                        }
                      />
                    </TableCell>
                                         <TableCell>
                       <Chip
                         label={user.status === 'ENABLED' ? 'Enabled' : user.status === 'DISABLED' ? 'Disabled' : user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                         color={getStatusColor(user.status)}
                         size="small"
                       />
                     </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <PermissionButton
                          permission="Administrator"
                          size="small"
                          startIcon={updatingUsers.has(user.userName) ? <CircularProgress size={16} /> : <EditIcon />}
                          variant="outlined"
                          onClick={() => handleEditUser(user)}
                          disabled={updatingUsers.has(user.userName)}
                        >
                          {updatingUsers.has(user.userName) ? 'Updating...' : 'Edit'}
                        </PermissionButton>
                        <PermissionButton
                          permission="Administrator"
                          size="small"
                          variant="outlined"
                          color={user.status.toUpperCase() === 'ENABLED' ? 'warning' : 'success'}
                          onClick={() => handleToggleUserStatus(user.userName)}
                          disabled={
                            // Administrators can't change their own status, but can change other administrators
                            isEditingOwnAdminAccount(user) ||
                            updatingUsers.has(user.userName)
                          }
                        >
                          {updatingUsers.has(user.userName) ? 'Updating...' : (user.status.toUpperCase() === 'ENABLED' ? 'Disable' : 'Enable')}
                        </PermissionButton>
                        
                        <PermissionButton
                          permission="Administrator"
                          size="small"
                          startIcon={updatingUsers.has(user.userName) ? <CircularProgress size={16} /> : <DeleteIcon />}
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteUser(user.userName)}
                          disabled={
                            // Administrators can't delete themselves, but can delete other administrators
                            isEditingOwnAdminAccount(user) ||
                            updatingUsers.has(user.userName)
                          }
                        >
                          {updatingUsers.has(user.userName) ? 'Deleting...' : 'Delete'}
                        </PermissionButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderSystemSettings = () => {
    const cronParts = parseCronExpression(scanSettings.cron);
    // Use selectedDays state instead of parsing on every render
    
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            System Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure system-wide settings and preferences.
          </Typography>

          <Grid container spacing={3}>
            {/* Scan Settings Block */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">
                        Scan Settings
                      </Typography>
                    </Box>
                    {!editingScanSettings && (
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditingScanSettings(true);
                          setHasChanges(false);
                          setTimezoneSearch('');
                        }}
                        disabled={isReadOnly || loadingScanSettings}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>

                  {loadingScanSettings ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>Loading scan settings...</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                                                  <Switch
                        checked={scanSettings.status === 'ENABLED'}
                        onChange={(e) => {
                          if (editingScanSettings) {
                            setScanSettings(prev => ({
                              ...prev,
                              status: e.target.checked ? 'ENABLED' : 'DISABLED'
                            }));
                            setHasChanges(true);
                          }
                        }}
                        disabled={!editingScanSettings}
                      />
                          }
                          label={`Automatic Scanning: ${scanSettings.status}`}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth disabled={!editingScanSettings}>
                          <InputLabel>Timezone</InputLabel>
                          <Select
                            value={scanSettings.timezone}
                            label="Timezone"
                            onChange={(e) => {
                              setScanSettings(prev => ({
                                ...prev,
                                timezone: e.target.value
                              }));
                              setHasChanges(true);
                            }}
                            onOpen={() => setTimezoneSearch('')}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300
                                }
                              },
                              disablePortal: true,
                              keepMounted: true
                            }}
                          >
                            {/* Search input inside dropdown */}
                            <Box 
                              sx={{ p: 1, pb: 0.5 }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Search timezones..."
                                value={timezoneSearch}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setTimezoneSearch(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                  }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ 
                                  minWidth: 200,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'background.paper',
                                    '&:hover': {
                                      backgroundColor: 'action.hover'
                                    }
                                  }
                                }}
                              />
                            </Box>
                            
                            <Divider />
                            
                            {timezones
                              .filter(tz => 
                                timezoneSearch === '' || 
                                tz.label.toLowerCase().includes(timezoneSearch.toLowerCase()) ||
                                tz.value.toLowerCase().includes(timezoneSearch.toLowerCase())
                              )
                              .map((tz) => (
                                <MenuItem key={tz.value} value={tz.value}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant="body2">{tz.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {tz.offset >= 0 ? `+${tz.offset}` : `${tz.offset}`}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Hour"
                          type="number"
                          inputProps={{ min: 0, max: 23 }}
                          value={cronParts.hour}
                          onChange={(e) => {
                            if (editingScanSettings) {
                              const hourValidation = validateHour(e.target.value);
                              setTimeValidation(prev => ({
                                ...prev,
                                hour: hourValidation
                              }));
                              
                              const newCron = buildCronExpression(
                                cronParts.minute,
                                e.target.value,
                                selectedDays
                              );
                              setScanSettings(prev => ({ ...prev, cron: newCron }));
                              setHasChanges(true);
                            }
                          }}
                          disabled={!editingScanSettings}
                          error={!timeValidation.hour.isValid}
                          helperText={timeValidation.hour.error || 'Hour (0-23)'}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Minute"
                          type="number"
                          inputProps={{ min: 0, max: 59 }}
                          value={cronParts.minute}
                          onChange={(e) => {
                            if (editingScanSettings) {
                              const minuteValidation = validateMinute(e.target.value);
                              setTimeValidation(prev => ({
                                ...prev,
                                minute: minuteValidation
                              }));
                              
                              const newCron = buildCronExpression(
                                e.target.value,
                                cronParts.hour,
                                selectedDays
                              );
                              setScanSettings(prev => ({ ...prev, cron: newCron }));
                              setHasChanges(true);
                            }
                          }}
                          disabled={!editingScanSettings}
                          error={!timeValidation.minute.isValid}
                          helperText={timeValidation.minute.error || 'Minute (0-59)'}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Days of Week {selectedDays.length === 0 ? '(Every Day)' : `(${selectedDays.length} selected)`}
                        </Typography>
                        <FormGroup row sx={{ mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedDays.length === 0 || selectedDays.length === 7}
                                onChange={(e) => {
                                  if (editingScanSettings) {
                                    if (e.target.checked) {
                                      // Select all days (which means every day - empty array)
                                      setSelectedDays([]);
                                      const newCron = buildCronExpression(cronParts.minute, cronParts.hour, []);
                                      setScanSettings(prev => ({ ...prev, cron: newCron }));
                                    } else {
                                      // If unchecking "Every Day", start with Monday selected
                                      const defaultDays = ['MON'];
                                      setSelectedDays(defaultDays);
                                      const newCron = buildCronExpression(cronParts.minute, cronParts.hour, defaultDays);
                                      setScanSettings(prev => ({ ...prev, cron: newCron }));
                                    }
                                    setHasChanges(true);
                                  }
                                }}
                                disabled={!editingScanSettings}
                              />
                            }
                            label="Every Day"
                            sx={{ minWidth: '110px', mr: 1 }}
                          />
                          {daysOfWeek.map((day) => (
                            <FormControlLabel
                              key={day.value}
                              control={
                                <Checkbox
                                  checked={selectedDays.length > 0 && selectedDays.includes(day.value)}
                                  onChange={(e) => {
                                    if (editingScanSettings) {
                                      let newSelectedDays: string[];
                                      if (e.target.checked) {
                                        // If currently "Every Day" is selected, start fresh with just this day
                                        if (selectedDays.length === 0) {
                                          newSelectedDays = [day.value];
                                        } else {
                                          newSelectedDays = [...selectedDays, day.value];
                                        }
                                      } else {
                                        newSelectedDays = selectedDays.filter(d => d !== day.value);
                                        // If no days left, go back to "Every Day"
                                        if (newSelectedDays.length === 0) {
                                          newSelectedDays = [];
                                        }
                                      }
                                      setSelectedDays(newSelectedDays);
                                      const newCron = buildCronExpression(cronParts.minute, cronParts.hour, newSelectedDays);
                                      setScanSettings(prev => ({ ...prev, cron: newCron }));
                                      setHasChanges(true);
                                    }
                                  }}
                                  disabled={!editingScanSettings}
                                />
                              }
                              label={day.label.substring(0, 3)}
                              sx={{ minWidth: '70px', mr: 0.5 }}
                            />
                          ))}
                        </FormGroup>
                      </Grid>





                      {editingScanSettings && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={savingScanSettings ? <CircularProgress size={16} /> : <SaveIcon />}
                              onClick={saveScanSettings}
                              disabled={savingScanSettings || !isFormValid()}
                            >
                              {savingScanSettings ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={cancelEditScanSettings}
                              disabled={savingScanSettings}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Scan limit events (resources excluded from scanning due to volume) */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventNoteIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Events</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Resources that were removed from automatic scanning after exceeding the configured resource limit for a scan.
                  </Typography>
                  {loadingScanEvents ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>Loading events...</Typography>
                    </Box>
                  ) : scanLimitEvents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No scan limit events.
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {scanLimitEvents.map((row, idx) => {
                        const aid = row.accountId ?? '';
                        const res = row.resource ?? '';
                        const n = row.observed_count ?? '';
                        const rowKey = `${aid}::${res}::${idx}`;
                        const ackKey = `${aid}::${res}`;
                        const canAck = Boolean(aid && res) && !isReadOnly;
                        return (
                          <ListItem
                            key={rowKey}
                            alignItems="flex-start"
                            sx={{
                              flexWrap: 'wrap',
                              gap: 1,
                              py: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 0 },
                            }}
                          >
                            <ListItemText
                              primary={`In account ${aid} resource ${res} was removed from scanning because of a large number of resources (${n}).`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                            <ListItemSecondaryAction>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => acknowledgeScanLimit(aid, res)}
                                disabled={!canAck || acknowledgingScanLimitKey === ackKey}
                                startIcon={
                                  acknowledgingScanLimitKey === ackKey ? (
                                    <CircularProgress size={14} />
                                  ) : undefined
                                }
                              >
                                {acknowledgingScanLimitKey === ackKey ? 'Working...' : 'Acknowledge'}
                              </Button>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAccountSettings = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Account Selection Block */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Selection
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  value={selectedAccountId}
                  onChange={(e) => handleSelectAccount(e.target.value as string)}
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.accountId} value={acc.accountId}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{acc.account_name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>{acc.accountId}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Allowed Regions Block */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Allowed Regions
            </Typography>
            {!editingAllowedRegions && selectedAccountId && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditAllowedRegions}
                disabled={isReadOnly || !selectedAccountId}
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          
          {selectedAccountId ? (
            editingAllowedRegions ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select regions to allow for this account:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {availableRegions.map((region) => (
                    <Chip
                      key={region}
                      label={region}
                      clickable
                      color={selectedRegions.includes(region) ? 'primary' : 'default'}
                      variant={selectedRegions.includes(region) ? 'filled' : 'outlined'}
                      onClick={() => handleRegionToggle(region)}
                    />
                  ))}
                </Box>

                {!selectedRegions.includes('us-east-1') && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      us-east-1 is not enabled
                    </Typography>
                    <Typography variant="body2">
                      Disabling <strong>us-east-1</strong> can affect monitoring and event-based features.
                    </Typography>
                  </Alert>
                )}
                {selectedRegions.length === 0 && (
                  <Typography variant="body2" color="error" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Please select at least one region.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveAllowedRegions}
                    disabled={savingAllowedRegions || selectedRegions.length === 0}
                    startIcon={savingAllowedRegions ? <CircularProgress size={16} /> : <SaveIcon />}
                  >
                    {savingAllowedRegions ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEditAllowedRegions}
                    disabled={savingAllowedRegions}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              (() => {
                const regions = getSelectedAccountRegions();
                return regions.length > 0 ? (
                  <Box>
                    {!regions.includes('us-east-1') && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          us-east-1 is not enabled
                        </Typography>
                        <Typography variant="body2">
                          <strong>us-east-1</strong> is disabled for this account. This can affect monitoring and event-based features.
                        </Typography>
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {regions.map((r) => (
                        <Chip key={r} label={r} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                );
              })()
            )
          ) : (
            <Typography variant="body2" color="text.secondary">Select an account to view regions</Typography>
          )}
        </CardContent>
      </Card>

      {/* us-east-1 disable confirmation */}
      <Dialog
        open={showDisableUsEast1Dialog}
        onClose={() => setShowDisableUsEast1Dialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable us-east-1?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This can affect monitoring
            </Typography>
            <Typography variant="body2">
              Disabling <strong>us-east-1</strong> may impact monitoring and event-based features.
            </Typography>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to disable <strong>us-east-1</strong> for this account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableUsEast1Dialog(false)} variant="outlined">
            Keep Enabled
          </Button>
          <Button
            onClick={() => {
              setSelectedRegions(prev => prev.filter(r => r !== 'us-east-1'));
              setShowDisableUsEast1Dialog(false);
            }}
            color="warning"
            variant="contained"
          >
            Disable us-east-1
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Allowed Services Block */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom>
                Allowed Services
              </Typography>
              <IconButton
                size="small"
                onClick={() => setAllowedServicesBlockCollapsed(!allowedServicesBlockCollapsed)}
                sx={{ p: 0.5 }}
              >
                {allowedServicesBlockCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!editingAllowedServices ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    // Clean up any invalid denied resources when starting to edit
                    const cleanedDenied = cleanupInvalidDeniedResources(
                      accountServiceSettings.allowed_resources,
                      accountServiceSettings.denied_resources
                    );
                    
                    // Update the state with cleaned denied resources
                    setAccountServiceSettings(prev => ({
                      ...prev,
                      denied_resources: cleanedDenied
                    }));
                    
                    setEditingAllowedServices(true);
                  }}
                  disabled={isReadOnly || !selectedAccountId || loadingAccountServiceSettings || loadingSupportedServices}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={savingAllowedServices ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={saveAllowedServices}
                    disabled={savingAllowedServices || !selectedAccountId}
                  >
                    {savingAllowedServices ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={cancelEditAllowedServices}
                    disabled={savingAllowedServices}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
          
          {!allowedServicesBlockCollapsed && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which AWS services and resources are allowed per onboarded account. Use Denied to exclude specific resources when selecting All.
              </Typography>


          <Box>
            {(loadingAccounts || loadingSupportedServices || loadingAccountServiceSettings) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading settings...</Typography>
              </Box>
            ) : !selectedAccountId ? (
              <Alert severity="info">Select an account to view and change allowed services.</Alert>
                ) : (
                  <>
                    {/* Enhanced Controls Bar */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        {/* Search */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search services or resources..."
                            value={serviceSearchTerm}
                            onChange={(e) => setServiceSearchTerm(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        
                        {/* View Mode Toggle */}
                        <Grid item xs={12} md={4}>
                          <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(_, newMode) => newMode && setViewMode(newMode)}
                            size="small"
                            fullWidth
                          >
                            <ToggleButton value="table">
                              <Tooltip title="Compact Table View">
                                <ViewListIcon />
                              </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="cards">
                              <Tooltip title="Grouped View">
                                <ViewModuleIcon />
                              </Tooltip>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Grid>
                        
                        {/* Bulk Actions */}
                        {editingAllowedServices && (
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={selectAllServices}
                                sx={{ 
                                  minWidth: 'auto',
                                  px: 1.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Select All
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={unselectAllServices}
                                sx={{ 
                                  minWidth: 'auto',
                                  px: 1.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Clear All
                              </Button>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      
                      {/* Results Summary */}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing {getFilteredServices().length} of {Object.keys(supportedServices).length} services
                          {serviceSearchTerm && ` matching "${serviceSearchTerm}"`}
                          {` • Sorted by ${serviceSortBy === 'name' ? 'Service Name' : serviceSortBy === 'resources' ? 'Resource Count' : 'Status'} (${serviceSortOrder === 'asc' ? 'Ascending' : 'Descending'})`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setCollapsedServices(new Set())}
                          >
                            Expand All
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setCollapsedServices(new Set(getFilteredServices().map(([service]) => service)))}
                          >
                            Collapse All
                          </Button>
                          {serviceSearchTerm && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => setServiceSearchTerm('')}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Services Display */}
                    {Object.keys(supportedServices).length === 0 ? (
                      <Alert severity="info">No supported services found.</Alert>
                    ) : getFilteredServices().length === 0 ? (
                      <Alert severity="info">
                        No services found matching your search criteria.
                        {serviceSearchTerm && ` Try searching for "${serviceSearchTerm}" with different terms.`}
                      </Alert>
                    ) : viewMode === 'table' ? (
                      /* Compact Table View */
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell 
                                sx={{ 
                                  cursor: 'pointer', 
                                  userSelect: 'none',
                                  backgroundColor: serviceSortBy === 'name' ? 'action.hover' : 'transparent',
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                                onClick={() => handleColumnSort('name')}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  Service
                                  {serviceSortBy === 'name' && (
                                    serviceSortOrder === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">All</TableCell>
                              <TableCell 
                                sx={{ 
                                  cursor: 'pointer', 
                                  userSelect: 'none',
                                  backgroundColor: serviceSortBy === 'resources' ? 'action.hover' : 'transparent',
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                                onClick={() => handleColumnSort('resources')}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  Resources
                                  {serviceSortBy === 'resources' && (
                                    serviceSortOrder === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ 
                                  cursor: 'pointer', 
                                  userSelect: 'none',
                                  backgroundColor: serviceSortBy === 'status' ? 'action.hover' : 'transparent',
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                                onClick={() => handleColumnSort('status')}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  Status
                                  {serviceSortBy === 'status' && (
                                    serviceSortOrder === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getFilteredServices().map(([service, resources]) => (
                              <React.Fragment key={service}>
                                <TableRow hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {service}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleServiceCollapsed(service)}
                                        sx={{ p: 0.5 }}
                                      >
                                        {collapsedServices.has(service) ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      checked={isServiceAllAllowed(service)}
                                      onChange={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleServiceAllAllow(service, e.target.checked);
                                      }}
                                      disabled={!editingAllowedServices}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {resources.length} resources
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {isServiceAllAllowed(service) ? (
                                        <Chip label="All Allowed" color="primary" size="small" />
                                      ) : (
                                        <>
                                          {resources.filter(r => isResourceAllowed(service, r)).slice(0, 2).map(r => (
                                            <Chip key={`status-${service}:${r}`} label={r} color="primary" size="small" />
                                          ))}
                                          {resources.filter(r => isResourceAllowed(service, r)).length > 2 && (
                                            <Chip 
                                              label={`+${resources.filter(r => isResourceAllowed(service, r)).length - 2} more`} 
                                              color="primary" 
                                              size="small" 
                                              variant="outlined"
                                            />
                                          )}
                                        </>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Expandable row for detailed resource management */}
                                {!collapsedServices.has(service) && (
                                  <TableRow>
                                    <TableCell colSpan={4} sx={{ py: 2, bgcolor: 'grey.50' }}>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                          Allowed resources
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                          {resources.map((res) => (
                                            <Chip
                                              key={`${service}:${res}`}
                                              label={res}
                                              variant={isResourceAllowed(service, res) || isServiceAllAllowed(service) ? 'filled' : 'outlined'}
                                              color={isResourceAllowed(service, res) || isServiceAllAllowed(service) ? 'primary' : 'default'}
                                              onClick={editingAllowedServices ? () => toggleResourceAllow(service, res, !(isResourceAllowed(service, res))) : undefined}
                                              sx={{ cursor: editingAllowedServices ? 'pointer' : 'default' }}
                                              size="small"
                                            />
                                          ))}
                                        </Box>
                                        
                                        {/* Denied resources section */}
                                        {isServiceAllAllowed(service) && resources.length > 1 && (
                                          <>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                              Denied resources (when All is selected)
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {resources.map((res) => (
                                                <Chip
                                                  key={`${service}:deny:${res}`}
                                                  label={res}
                                                  variant={isResourceDenied(service, res) ? 'filled' : 'outlined'}
                                                  color={isResourceDenied(service, res) ? 'error' : 'default'}
                                                  onClick={editingAllowedServices ? () => toggleResourceDeny(service, res, !(isResourceDenied(service, res))) : undefined}
                                                  sx={{ cursor: editingAllowedServices ? 'pointer' : 'default' }}
                                                  size="small"
                                                />
                                              ))}
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      /* Grouped View */
                      <Box>
                        {Object.entries(getGroupedServices()).map(([groupName, services]) => (
                          <Card key={groupName} variant="outlined" sx={{ mb: 3 }}>
                            <CardContent sx={{ p: 0 }}>
                              {/* Group Header */}
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: groupName === 'Enabled Services' ? 'primary.50' : 'grey.50', 
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="h6" sx={{ 
                                    fontWeight: 600, 
                                    color: groupName === 'Enabled Services' ? 'primary.main' : 'text.primary'
                                  }}>
                                    {groupName}
                                  </Typography>
                                  <Chip 
                                    label={`${services.length} service${services.length !== 1 ? 's' : ''}`} 
                                    size="small" 
                                    variant="outlined"
                                    color={groupName === 'Enabled Services' ? 'primary' : 'default'}
                                  />
                                </Box>
                              </Box>

                              {/* Services in Category */}
                              <Box sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                  {services.map(([service, resources]) => (
                                    <Grid item xs={12} sm={6} md={4} key={service}>
                                      <Card variant="outlined" sx={{ 
                                        height: '100%',
                                        border: isServiceAllAllowed(service) || 
                                          resources.some(r => isResourceAllowed(service, r)) ? 
                                          '2px solid' : '1px solid',
                                        borderColor: isServiceAllAllowed(service) || 
                                          resources.some(r => isResourceAllowed(service, r)) ? 
                                          'primary.main' : 'divider',
                                        bgcolor: isServiceAllAllowed(service) || 
                                          resources.some(r => isResourceAllowed(service, r)) ? 
                                          'primary.50' : 'background.paper'
                                      }}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                          {/* Service Header */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                {service}
                                              </Typography>
                                              <IconButton
                                                size="small"
                                                onClick={() => toggleServiceCollapsed(service)}
                                                sx={{ p: 0.5 }}
                                              >
                                                {collapsedServices.has(service) ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                                              </IconButton>
                                            </Box>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={isServiceAllAllowed(service)}
                                                  onChange={(e) => toggleServiceAllAllow(service, e.target.checked)}
                                                  disabled={!editingAllowedServices}
                                                  size="small"
                                                />
                                              }
                                              label="All"
                                              sx={{ m: 0 }}
                                            />
                                          </Box>

                                          {/* Service Status Summary */}
                                          <Box sx={{ mb: 1.5 }}>
                                            {isServiceAllAllowed(service) ? (
                                              <Chip label="All Allowed" color="primary" size="small" variant="filled" />
                                            ) : (
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {resources.filter(r => isResourceAllowed(service, r)).slice(0, 2).map(r => (
                                                  <Chip key={`status-${service}:${r}`} label={r} color="primary" size="small" />
                                                ))}
                                                {resources.filter(r => isResourceAllowed(service, r)).length > 2 && (
                                                  <Chip 
                                                    label={`+${resources.filter(r => isResourceAllowed(service, r)).length - 2} more`} 
                                                    color="primary" 
                                                    size="small" 
                                                    variant="outlined"
                                                  />
                                                )}
                                                {resources.filter(r => isResourceAllowed(service, r)).length === 0 && (
                                                  <Chip label="None Allowed" color="default" size="small" variant="outlined" />
                                                )}
                                              </Box>
                                            )}
                                          </Box>

                                          {/* Expandable Details */}
                                          {!collapsedServices.has(service) && (
                                            <Box>
                                              {/* Allowed Resources */}
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                Allowed Resources
                                              </Typography>
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                {resources.map((res) => (
                                                  <Chip
                                                    key={`${service}:${res}`}
                                                    label={res}
                                                    variant={isResourceAllowed(service, res) || isServiceAllAllowed(service) ? 'filled' : 'outlined'}
                                                    color={isResourceAllowed(service, res) || isServiceAllAllowed(service) ? 'primary' : 'default'}
                                                    onClick={editingAllowedServices ? () => toggleResourceAllow(service, res, !(isResourceAllowed(service, res))) : undefined}
                                                    sx={{ cursor: editingAllowedServices ? 'pointer' : 'default' }}
                                                    size="small"
                                                  />
                                                ))}
                                              </Box>

                                              {/* Denied Resources (only when All is selected) */}
                                              {isServiceAllAllowed(service) && resources.length > 1 && (
                                                <>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                    Denied Resources
                                                  </Typography>
                                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {resources.map((res) => (
                                                      <Chip
                                                        key={`${service}:deny:${res}`}
                                                        label={res}
                                                        variant={isResourceDenied(service, res) ? 'filled' : 'outlined'}
                                                        color={isResourceDenied(service, res) ? 'error' : 'default'}
                                                        onClick={editingAllowedServices ? () => toggleResourceDeny(service, res, !(isResourceDenied(service, res))) : undefined}
                                                        sx={{ cursor: editingAllowedServices ? 'pointer' : 'default' }}
                                                        size="small"
                                                      />
                                                    ))}
                                                  </Box>
                                                </>
                                              )}
                                            </Box>
                                          )}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </>
            )}
          </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );



  // Only show loading skeleton for User Management tab when loading
  if (loading && currentTabFromUrl === TAB_NAMES.USER_MANAGEMENT) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          Settings
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTabFromUrl} onChange={handleTabChange}>
            <Tab label="User Management" value={TAB_NAMES.USER_MANAGEMENT} />
            <Tab label="System Settings" value={TAB_NAMES.SYSTEM_SETTINGS} />
            <Tab label="Account Settings" value={TAB_NAMES.ACCOUNT_SETTINGS} />
            <Tab label="Subscription" value={TAB_NAMES.SUBSCRIPTION} />
          </Tabs>
        </Box>

        {/* User Management Tab Skeleton */}
        <Card sx={{ mt: 0 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5">
                  Users
                </Typography>
                <Skeleton variant="text" width="120px" height={20} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>

            {/* Filter Section Skeleton */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
              </Grid>
            </Box>

            {/* Users Table Skeleton */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={16} height={16} />
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Skeleton variant="rectangular" width={16} height={16} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                          <Box>
                            <Skeleton variant="text" width="120px" height={20} />
                            <Skeleton variant="text" width="80px" height={16} />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="150px" height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                          <Skeleton variant="text" width="60px" height={20} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Settings
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTabFromUrl} onChange={handleTabChange}>
          <Tab label="User Management" value={TAB_NAMES.USER_MANAGEMENT} />
          <Tab label="System Settings" value={TAB_NAMES.SYSTEM_SETTINGS} />
          <Tab label="Account Settings" value={TAB_NAMES.ACCOUNT_SETTINGS} />
          <Tab label="Subscription" value={TAB_NAMES.SUBSCRIPTION} />
          <Tab label="API Tokens" value={TAB_NAMES.API_TOKENS} />
        </Tabs>
      </Box>

      {/* User Management Tab */}
      <TabPanel value={currentTabFromUrl} index={TAB_NAMES.USER_MANAGEMENT}>
        {renderUserManagement()}
      </TabPanel>

      {/* System Settings Tab */}
      <TabPanel value={currentTabFromUrl} index={TAB_NAMES.SYSTEM_SETTINGS}>
        {renderSystemSettings()}
      </TabPanel>

      {/* Account Settings Tab */}
      <TabPanel value={currentTabFromUrl} index={TAB_NAMES.ACCOUNT_SETTINGS}>
        {renderAccountSettings()}
      </TabPanel>

      {/* Subscription Tab - Visible to all users */}
      <TabPanel value={currentTabFromUrl} index={TAB_NAMES.SUBSCRIPTION}>
        <Subscription />
      </TabPanel>

      <TabPanel value={currentTabFromUrl} index={TAB_NAMES.API_TOKENS}>
        <ApiTokens />
      </TabPanel>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onClose={() => setShowInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@company.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                placeholder="John"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                placeholder="Doe"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  label="Role"
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'Administrator' | 'PowerUser' | 'ReadOnly' })}
                >
                  <MenuItem value="ReadOnly">Read Only</MenuItem>
                  <MenuItem value="PowerUser">Power User</MenuItem>
                  <MenuItem value="Administrator">Administrator</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                     <PermissionButton
             permission="Administrator"
             onClick={handleInviteUser}
             variant="contained"
             disabled={!newUser.email || !newUser.firstName || !newUser.lastName}
             startIcon={loading ? <CircularProgress size={16} /> : undefined}
           >
             {loading ? 'Sending...' : 'Send Invitation'}
           </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              {isEditingOwnAdminAccount(selectedUser) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You are editing your own administrator account. Some fields are restricted for security reasons.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={selectedUser.email}
                  disabled
                  helperText={isEditingOwnAdminAccount(selectedUser) ? 
                    "Administrators cannot change their own email" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={selectedUser.firstName}
                  onChange={(e) => setSelectedUser({ ...selectedUser, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={selectedUser.lastName}
                  onChange={(e) => setSelectedUser({ ...selectedUser, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedUser.role}
                    label="Role"
                    onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value as 'Administrator' | 'PowerUser' | 'ReadOnly' })}
                    disabled={isEditingOwnAdminAccount(selectedUser)}
                  >
                    <MenuItem value="ReadOnly">Read Only</MenuItem>
                    <MenuItem value="PowerUser">Power User</MenuItem>
                    <MenuItem value="Administrator">Administrator</MenuItem>
                  </Select>
                  {isEditingOwnAdminAccount(selectedUser) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Administrators cannot change their own role
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedUser.status}
                    label="Status"
                    onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value as 'ENABLED' | 'DISABLED' })}
                    disabled={isEditingOwnAdminAccount(selectedUser)}
                  >
                    <MenuItem value="ENABLED">Enabled</MenuItem>
                    <MenuItem value="DISABLED">Disabled</MenuItem>
                  </Select>
                  {isEditingOwnAdminAccount(selectedUser) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Administrators cannot change their own status
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
                     <PermissionButton
             permission="Administrator"
             onClick={handleUpdateUser}
             variant="contained"
             disabled={selectedUser ? updatingUsers.has(selectedUser.userName) : false}
             startIcon={selectedUser && updatingUsers.has(selectedUser.userName) ? <CircularProgress size={16} /> : undefined}
           >
             {selectedUser && updatingUsers.has(selectedUser.userName) ? 'Updating...' : 'Update User'}
           </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Mass Edit Dialog */}
      <Dialog open={showMassEditDialog} onClose={() => setShowMassEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mass Edit Users ({selectedUsers.size} selected)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={massEditAction}
                  label="Action"
                  onChange={(e) => setMassEditAction(e.target.value as 'role' | 'status')}
                >
                  <MenuItem value="role">Change Role</MenuItem>
                  <MenuItem value="status">Change Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {massEditAction === 'role' ? (
                <FormControl fullWidth>
                  <InputLabel>New Role</InputLabel>
                  <Select
                    value={massEditValue}
                    label="New Role"
                    onChange={(e) => setMassEditValue(e.target.value)}
                  >
                                      <MenuItem value="ReadOnly">Read Only</MenuItem>
                  <MenuItem value="PowerUser">Power User</MenuItem>
                  <MenuItem value="Administrator">Administrator</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>New Status</InputLabel>
                  <Select
                    value={massEditValue}
                    label="New Status"
                    onChange={(e) => setMassEditValue(e.target.value)}
                  >
                                      <MenuItem value="ENABLED">Enabled</MenuItem>
                  <MenuItem value="DISABLED">Disabled</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMassEditDialog(false)}>Cancel</Button>
                     <PermissionButton
             permission="Administrator"
             onClick={handleMassEdit}
             variant="contained"
             disabled={!massEditValue || Array.from(selectedUsers).some(userId => updatingUsers.has(userId))}
             startIcon={Array.from(selectedUsers).some(userId => updatingUsers.has(userId)) ? <CircularProgress size={16} /> : undefined}
           >
             {Array.from(selectedUsers).some(userId => updatingUsers.has(userId)) ? 'Updating...' : `Update ${selectedUsers.size} Users`}
           </PermissionButton>
        </DialogActions>
      </Dialog>


    </Box>
  );
};
